/**
 * The sentence builder (captures `11`, `20`, `25`, `30`, `37`, `40`, `50`).
 *
 * Layout, measured from the captures: `‹ Back` at the top left; the sentence at x ≈ 344 in the
 * 36 px title face; each node's `+` and 🗑 affordances right-aligned at x ≈ 1184 / 1236; a green
 * `↓` between the trigger block (trigger + its conditions) and the actions; and the blue
 * **Create automation** button under the last node — **inert until every placeholder has been
 * chosen**, `isCondition` included (INDEX.md). A pre-built template shows the blue
 * "This is a pre-built template." banner instead of the add/delete affordances (capture 11).
 */
import {
  automationBlockRegistry,
  isActionBlock,
  isBlockAvailable,
  isConditionBlock,
  parseSentence,
  isSlotFilled,
  recipeCompleteness,
  type ActionNode,
  type AutomationRecipe,
  type ConditionNode,
  type TriggerNode,
} from '@elizmonday/shared';
import { useMemo, useRef, useState } from 'react';
import { Icon, icons } from '@elizmonday/ui';
import { SlotChip, type SlotChipProps } from './slots';
/** The card and the editor read a node's column the same way (T-8d). */
import { columnOfNode } from './sentence';
import { SlotPopover, PopoverCaption, PopoverOption } from './slots/SlotPopover';
import type { PickerDataView } from './types';

export interface RecipeEditorProps {
  recipe: AutomationRecipe;
  pickers: PickerDataView;
  onChange(next: AutomationRecipe): void;
  onBack(): void;
  onSubmit(): void;
  /** "Create automation" while adding, "Save" while editing an existing one. */
  submitLabel?: string;
  /** A `static` template's sentence is fixed: no node may be added or removed (capture 11). */
  fixedShape?: boolean;
  busy?: boolean;
  error?: string | null;
  /**
   * P-200: the column picker's **+ Add a new column** row. The editor owns no board id and no
   * reload, so the host supplies the handler and every column slot in the sentence gets it.
   */
  onAddColumn?: SlotChipProps['onAddColumn'];
}

type AnyNode = TriggerNode | ConditionNode | ActionNode;

const boardColumnTypes = (pickers: PickerDataView): string[] => [
  ...new Set([...pickers.columns, ...pickers.subitemColumns].map((column) => column.type)),
];

/**
 * Blocks this board can run: monday hides a recipe whose column type the board lacks, and we
 * never offer an action nothing implements. The predicate is the registry's own
 * (`isBlockAvailable`), so the api's `automations.catalog` answers the same question with the
 * same code rather than a second copy of half of it (**P-201**).
 */
export function availableBlocks(pickers: PickerDataView, kind: 'condition' | 'action') {
  const types = boardColumnTypes(pickers);
  return automationBlockRegistry
    .list()
    .filter((block) => (kind === 'condition' ? isConditionBlock(block) : isActionBlock(block)))
    .filter((block) => isBlockAvailable(block, types));
}

function nextNodeId(recipe: AutomationRecipe): string {
  const used = new Set([recipe.trigger.nodeId, ...recipe.conditions.map((n) => n.nodeId), ...recipe.actions.map((n) => n.nodeId)]);
  for (let index = 0; ; index += 1) {
    const candidate = `n${String(index)}`;
    if (!used.has(candidate)) return candidate;
  }
}

export function RecipeEditor({
  recipe,
  pickers,
  onChange,
  onBack,
  onSubmit,
  submitLabel = 'Create automation',
  fixedShape = false,
  busy = false,
  error = null,
  onAddColumn,
}: RecipeEditorProps) {
  const completeness = useMemo(() => recipeCompleteness(recipe), [recipe]);
  const [adding, setAdding] = useState<{ kind: 'condition' | 'action' } | null>(null);
  const addAnchor = useRef<HTMLButtonElement>(null);

  const patchParams = (nodeId: string, param: string, value: unknown): void => {
    const patch = <T extends AnyNode>(node: T): T =>
      node.nodeId === nodeId ? ({ ...node, params: { ...node.params, [param]: value } } as T) : node;
    onChange({
      ...recipe,
      trigger: patch(recipe.trigger),
      conditions: recipe.conditions.map(patch),
      actions: recipe.actions.map(patch),
    });
  };

  const removeNode = (nodeId: string): void => {
    onChange({
      ...recipe,
      conditions: recipe.conditions.filter((node) => node.nodeId !== nodeId),
      actions: recipe.actions.filter((node) => node.nodeId !== nodeId),
    });
  };

  const addNode = (kind: 'condition' | 'action', blockId: string): void => {
    const nodeId = nextNodeId(recipe);
    if (kind === 'condition') {
      onChange({ ...recipe, conditions: [...recipe.conditions, { nodeId, block: blockId, params: {}, inputs: {} }] });
    } else {
      onChange({ ...recipe, actions: [...recipe.actions, { nodeId, block: blockId, params: {}, inputs: {}, delay: null }] });
    }
    setAdding(null);
  };

  const renderNode = (node: AnyNode, kind: 'trigger' | 'condition' | 'action', first = false) => {
    const block = automationBlockRegistry.find(node.block);
    if (block === undefined) {
      return (
        <div key={node.nodeId} className="ez-automation-node" data-kind={kind} data-testid={`recipe-node-${node.nodeId}`}>
          <span className="ez-automation-node__sentence ez-automation-node__sentence--unmapped">{node.block}</span>
        </div>
      );
    }
    const columnId = columnOfNode(node);
    return (
      <div key={node.nodeId} className="ez-automation-node" data-kind={kind} data-testid={`recipe-node-${node.nodeId}`}>
        <span className="ez-automation-node__sentence">
          {/* monday prefixes the first action with "Then " — it is not in any block's own
              partial (captures `20-editor-dupgroup`, `50-editor-277`, `39-message-editor`). */}
          {kind === 'action' && first ? <span className="ez-automation-term">Then </span> : null}
          {parseSentence(block).map((part, index) =>
            part.kind === 'text' ? (
              <span key={`t${String(index)}`} className="ez-automation-term">
                {part.text}
              </span>
            ) : (
              <SlotChip
                key={part.param + String(index)}
                kind={part.slot.kind}
                label={part.label}
                value={node.params[part.param]}
                filled={isSlotFilled(node.params[part.param])}
                pickers={pickers}
                columnId={columnId}
                columnTypes={part.slot.columnTypes ?? block.requiredColumnTypes}
                testId={`slot-${node.nodeId}-${part.param}`}
                onAddColumn={onAddColumn}
                onChange={(next) => patchParams(node.nodeId, part.param, next)}
              />
            ),
          )}
        </span>
        {fixedShape ? null : (
          <span className="ez-automation-node__actions">
            <button
              type="button"
              className="ez-automation-node__icon"
              aria-label={kind === 'trigger' ? 'Add a condition' : 'Add an action'}
              data-testid={`recipe-add-${node.nodeId}`}
              ref={kind === 'trigger' ? addAnchor : undefined}
              onClick={() => setAdding({ kind: kind === 'trigger' ? 'condition' : 'action' })}
            >
              <Icon icon={icons.add} size={16} ignoreFocusStyle />
            </button>
            {kind === 'trigger' ? null : (
              <button
                type="button"
                className="ez-automation-node__icon"
                aria-label="Delete this step"
                data-testid={`recipe-remove-${node.nodeId}`}
                onClick={() => removeNode(node.nodeId)}
              >
                <Icon icon={icons.delete} size={16} ignoreFocusStyle />
              </button>
            )}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="ez-automation-editor" data-testid="automation-editor">
      <header className="ez-automation-editor__header">
        <button type="button" className="ez-automation-back" onClick={onBack} data-testid="automation-editor-back">
          <Icon icon={icons.chevronLeft} size={16} ignoreFocusStyle />
          Back
        </button>
      </header>

      {fixedShape ? (
        <div className="ez-automation-banner" role="status" data-testid="automation-template-banner">
          <Icon icon={icons.description} size={16} ignoreFocusStyle />
          This is a pre-built template.
        </div>
      ) : null}

      <div className="ez-automation-editor__body">
        <div className="ez-automation-block">
          {renderNode(recipe.trigger, 'trigger')}
          {recipe.conditions.map((node) => renderNode(node, 'condition'))}
        </div>

        {recipe.actions.length === 0 ? null : (
          <div className="ez-automation-arrow" aria-hidden="true">
            ↓
          </div>
        )}

        <div className="ez-automation-block">
          {recipe.actions.map((node, index) => renderNode(node, 'action', index === 0))}
        </div>

        {fixedShape ? null : (
          <div className="ez-automation-editor__add-row">
            <button
              type="button"
              className="ez-automation-link"
              data-testid="automation-add-condition"
              ref={addAnchor}
              onClick={() => setAdding({ kind: 'condition' })}
            >
              Add a condition
            </button>
            <button
              type="button"
              className="ez-automation-link"
              data-testid="automation-add-action"
              onClick={() => setAdding({ kind: 'action' })}
            >
              Add an action
            </button>
          </div>
        )}

        {error === null ? null : (
          <div className="ez-automation-error" role="alert" data-testid="automation-editor-error">
            {error}
          </div>
        )}

        <button
          type="button"
          className="ez-automation-primary"
          disabled={!completeness.complete || busy || recipe.actions.length === 0}
          data-testid="automation-editor-submit"
          onClick={onSubmit}
        >
          {submitLabel}
        </button>
      </div>

      {adding === null ? null : (
        <SlotPopover
          anchorRef={addAnchor}
          label={adding.kind === 'condition' ? 'Add a condition' : 'Add an action'}
          width={320}
          onDismiss={() => setAdding(null)}
          testId="automation-add-popover"
        >
          <PopoverCaption>{adding.kind === 'condition' ? 'Add a condition' : 'Add an action'}</PopoverCaption>
          {availableBlocks(pickers, adding.kind).map((block) => (
            <PopoverOption
              key={block.id}
              testId={`automation-add-${block.id}`}
              onClick={() => addNode(adding.kind, block.id)}
            >
              {block.sentencePartial.replace(/\{([^{},]*),\s*[A-Za-z0-9_]+\}/g, '$1')}
            </PopoverOption>
          ))}
        </SlotPopover>
      )}
    </div>
  );
}
